import { useQuery } from '@tanstack/react-query';
import { getFolderContents, listRootFolders } from '@/api/dms/generated/sdk.gen';
import type {
  PermissionResourcesContext,
  ResourceItem,
  ResourceSectionData,
} from '@/components/permissions/types';
import { FEATURE_IDS } from '@/features/dms/constants/permissions';

export function useDmsPermissionResources(ctx: PermissionResourcesContext): ResourceSectionData[] {
  const { mode, accessibleResourceIds } = ctx;
  const isViewMode = mode === 'view';
  const hasResourcePerms = accessibleResourceIds.size > 0;

  // Fetch root folders
  const rootFoldersQuery = useQuery({
    queryKey: ['folders', 'for-permissions', 'root', mode],
    queryFn: async () => {
      const { data } = await listRootFolders({
        query: { page: 1, per_page: 200 },
        throwOnError: true,
      });
      return data;
    },
    enabled: !isViewMode || hasResourcePerms,
    staleTime: 5 * 60 * 1000,
    select: (data) => data?.items ?? [],
  });

  const rootFolders = rootFoldersQuery.data ?? [];

  // Fetch subfolders for each root folder
  const subFoldersQuery = useQuery({
    queryKey: ['folders', 'for-permissions', 'children', rootFolders.map((f) => f.id)],
    queryFn: async () => {
      const results = await Promise.all(
        rootFolders.map((folder) =>
          getFolderContents({
            path: { id: folder.id },
            query: { page: 1, per_page: 200 },
            throwOnError: true,
          }).then((res) =>
            (res.data?.subfolders?.items ?? []).map((sf) => ({ ...sf, _parentId: folder.id }))
          )
        )
      );
      return results.flat();
    },
    enabled: rootFolders.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Build folder items: roots (no parentId) + children (with parentId)
  const allFolders: ResourceItem[] = [
    ...rootFolders.map((f) => ({
      id: f.id,
      name: f.name,
      resourceType: 'folder',
    })),
    ...(subFoldersQuery.data ?? []).map((sf) => ({
      id: sf.id,
      name: sf.name,
      resourceType: 'folder',
      parentId: sf._parentId,
      parentResourceType: 'folder',
    })),
  ];

  const filteredFolders = isViewMode
    ? allFolders.filter((f) => accessibleResourceIds.has(f.id))
    : allFolders;

  // Static feature items
  const featureItems: ResourceItem[] = [
    // User-facing modules
    { id: FEATURE_IDS.PURCHASING, name: 'Purchasing', resourceType: 'feature' },
    { id: FEATURE_IDS.CAPAS, name: 'CAPAs', resourceType: 'feature' },
    { id: FEATURE_IDS.ECRS, name: 'ECRs', resourceType: 'feature' },
    { id: FEATURE_IDS.CALIBRATION, name: 'Calibration', resourceType: 'feature' },
    { id: FEATURE_IDS.TESTING, name: 'Testing', resourceType: 'feature' },
    // Admin / configuration
    { id: FEATURE_IDS.DOCUMENT_TYPES, name: 'Document Types', resourceType: 'feature' },
    { id: FEATURE_IDS.PDF_GENERATION, name: 'PDF Generation', resourceType: 'feature' },
    { id: FEATURE_IDS.PURCHASING_SETTINGS, name: 'Purchasing Settings', resourceType: 'feature' },
    { id: FEATURE_IDS.CAPA_SETTINGS, name: 'CAPA Settings', resourceType: 'feature' },
    { id: FEATURE_IDS.ATE_TOKENS, name: 'ATE Tokens', resourceType: 'feature' },
  ];

  const filteredFeatures = isViewMode
    ? featureItems.filter((f) => accessibleResourceIds.has(f.id))
    : featureItems;

  return [
    {
      resourceType: 'folder',
      items: filteredFolders,
      isLoading: rootFoldersQuery.isLoading || subFoldersQuery.isLoading,
    },
    {
      resourceType: 'feature',
      items: filteredFeatures,
      isLoading: false,
    },
  ];
}
